import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ReferenceUploader } from "./ReferenceUploader";
import { Layers, Loader2, Upload } from "lucide-react";
import { BatchList } from "./BatchList";

const TYPES = [
  { id: "unboxing", label: "Unboxing" },
  { id: "product_launch", label: "Product launch" },
  { id: "selfie_review", label: "Selfie review" },
];
const field = "w-full text-sm border border-slate-300 rounded-md px-2.5 py-2 bg-white text-slate-900 placeholder:text-slate-400";
const splitScripts = (t) => t.split(/\n\s*---+\s*\n/).map((s) => s.trim()).filter(Boolean);

export const BatchButton = ({ onOpenContent }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [f, setF] = useState({ name: "", template: "unboxing", brand_id: "", influencer_id: "", product_name: "", cta_text: "", attachments: [] });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands").then((r) => r.data) });
  const { data: infs = [] } = useQuery({ queryKey: ["influencers"], queryFn: () => api.get("/influencers").then((r) => r.data) });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const scripts = splitScripts(text);
  const hasProduct = f.attachments.some((a) => a.kind === "product" || a.kind === "charger");
  const ready = f.name.trim() && f.brand_id && f.influencer_id && f.product_name.trim() && hasProduct && scripts.length > 0 && scripts.length <= 1000;

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/studio/batches/parse-file", fd);
      setText(data.scripts.join("\n---\n"));
      toast.success(`${data.scripts.length} scripts imported${data.truncated ? " (first 1,000 kept)" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that file");
    }
    e.target.value = "";
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/batches", {
        ...f, scripts, attachments: f.attachments.map((a) => ({ id: a.id, kind: a.kind || "product" })),
      });
      toast.success(`Batch started — ${data.batch.total} videos queued`);
      qc.invalidateQueries({ queryKey: ["batches"] });
      setOpen(false);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the batch");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button data-testid="batch-open-btn" size="sm" variant="outline" className="h-8 text-xs w-full mb-3 border-blue-600 text-blue-700 hover:bg-blue-50" onClick={() => setOpen(true)}>
        <Layers className="w-3.5 h-3.5 mr-1.5" /> Batch production (up to 1,000 videos)
      </Button>
      <BatchList onOpenContent={onOpenContent} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="batch-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900 border-slate-200">
          <DialogHeader><DialogTitle>Batch production</DialogTitle></DialogHeader>
          <input data-testid="batch-name" className={field} placeholder="Batch name (e.g. HearClear June unboxings)" value={f.name} onChange={set("name")} />
          <div className="grid grid-cols-3 gap-2 mt-3">
            {TYPES.map((t) => (
              <button key={t.id} data-testid={`batch-type-${t.id}`} onClick={() => setF((s) => ({ ...s, template: t.id }))}
                className={`text-sm font-semibold rounded-md border p-2 transition-colors ${f.template === t.id ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <select data-testid="batch-brand" className={field} value={f.brand_id} onChange={set("brand_id")}>
              <option value="">Brand…</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select data-testid="batch-presenter" className={field} value={f.influencer_id} onChange={set("influencer_id")}>
              <option value="">Presenter…</option>
              {infs.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <input data-testid="batch-product" className={field} placeholder="Product name" value={f.product_name} onChange={set("product_name")} />
            <input data-testid="batch-cta" className={field} placeholder="End card CTA (website / phone)" value={f.cta_text} onChange={set("cta_text")} />
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="text-xs uppercase font-mono tracking-widest text-slate-500">Your scripts · one video each</div>
            <label data-testid="batch-import-label" className="text-xs text-blue-700 cursor-pointer flex items-center gap-1 hover:underline">
              <Upload className="w-3.5 h-3.5" /> Import CSV / TXT
              <input data-testid="batch-import-input" type="file" accept=".csv,.txt" className="hidden" onChange={importFile} />
            </label>
          </div>
          <textarea data-testid="batch-scripts" className={`${field} mt-1.5 h-40 font-mono text-xs`} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={"Exactly what the presenter says in video 1.\n---\nExactly what the presenter says in video 2.\n---\n…"} />
          <div data-testid="batch-script-count" className="text-xs text-slate-600 mt-1">
            {scripts.length} script{scripts.length === 1 ? "" : "s"} {scripts.length > 1000 && <span className="text-red-600">· maximum is 1,000</span>}
          </div>
          <div className="mt-3 text-xs uppercase font-mono tracking-widest text-slate-500">Genuine product photos (required)</div>
          <ReferenceUploader attachments={f.attachments} onChange={(fn) => setF((s) => ({ ...s, attachments: fn(s.attachments) }))} />
          <p className="text-[11px] text-slate-500 mt-2">
            Each video is voiced from your script and the presenter's face is animated from that voice, so lips match every word. Tokens are taken per video as it renders and refunded if a video fails; the batch pauses if your wallet runs low.
          </p>
          <Button data-testid="batch-create-btn" onClick={create} disabled={!ready || busy} className="mt-3 w-full">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Queuing…</> : `Start batch · ${scripts.length} videos`}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
